import React, { useEffect, useMemo, useState } from "react";
import * as s from "../SignupStyled";
import { ModalInput } from "components/atoms/input/modal/ModalInput";
import {
  CommonResponse,
  CustomError,
  Validate,
  ValidateMsg,
} from "api/interfaces/commonInterface.interface";
import BigButton from "components/atoms/buttons/BigButton";
import AddressInput, {
  AddressComponents,
} from "components/atoms/input/address/AddressInput";
import OrgName from "../common/OrgName";
import {
  FreeTrialInfo,
  PostResendCordResponse,
  SIGNUP_STEP,
  SIGNUP_UPDATE_TYPE,
  SignupUserInfo,
} from "api/interfaces/authInterface.interface";
import InputItem from "../common/InputItem";
import Phone from "../common/Phone";
import { checkEmailRegEx, orgNameAlphabetRule, orgNameRule } from "utils/regEx";
import useApiError from "hook/useApiError";
import useStatusMutation from "hook/useStatusMutation";
import { postFreeTrialSignUp } from "api/authAPI";
import { isPhoneNumberValid } from "utils/validHelper";
import * as ls from "../../signin/LoginStyled";
import { UnderlineText } from "components/atoms/text/Text";
import { COLORS } from "styles/colors";
import { handleDuplicateAccountName } from "utils/AccountUtil";
import { checkEmpty, toTrim } from "utils/functions";

interface Props {
  validateInputMsg: ValidateMsg;
  userInfo: SignupUserInfo;
  onChangeUserInfo: (type: SIGNUP_UPDATE_TYPE, data: string | boolean) => void;
  setStep: (step: SIGNUP_STEP) => void;
  setIsValidated: React.Dispatch<React.SetStateAction<Validate>>;
  setIsResend: React.Dispatch<React.SetStateAction<boolean>>;
  isResend: boolean;
  validateMsg: string;
  setValidateMsg: React.Dispatch<React.SetStateAction<string>>;
  setValidateInputMsg: React.Dispatch<React.SetStateAction<ValidateMsg>>;
  setCheckedEmail: React.Dispatch<React.SetStateAction<string>>;
  onClickBackLogin: () => void;
}

const intitalValidateMsg: ValidateMsg = {
  email: "",
  firstName: "",
  lastName: "",
  phoneNumber: "",
  address: "",
  orgName: "",
  businessType: "",
};
export default function FreeTrial(props: Props) {
  const [freeTrialInfo, setFreeTrialInfo] = useState<FreeTrialInfo>({
    email: "",
    firstName: "",
    lastName: "",
    phone: "",
    address: "",
    orgName: "",
    isCP: false,
    businessType: "",
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  });
  const userInfo = useMemo(() => {
    return {
      ...freeTrialInfo,
      firstName: props.userInfo.firstName,
      lastName: props.userInfo.lastName,
      phone: props.userInfo.phoneNumber,
    };
  }, [
    freeTrialInfo,
    props.userInfo.firstName,
    props.userInfo.lastName,
    props.userInfo.phoneNumber,
  ]);
  const { handleErrorNotify, lang, createCustomError, intlString } =
    useApiError();
  const mutationRequestTrial = useStatusMutation(postFreeTrialSignUp, {
    onSuccess: (res: PostResendCordResponse) => {
      if (res.error !== 0 && res.error !== 1029) {
        const resError: CustomError = createCustomError(
          res as unknown as CommonResponse,
          lang.validateMsg.signup.defaultError
        );
        handleErrorNotify(resError, lang.validateMsg.signup.defaultError);
        return;
      }
      props.setCheckedEmail(toTrim(userInfo.email));
      if (res.error === 1029) {
        props.setIsResend(true);
      }
      props.setStep(SIGNUP_STEP.TRIAL_SEND_EMAIL);
    },
    onError: (error: CustomError) => {
      if (error.response?.data.error === 3008) {
        handleDuplicateAccountName({
          setFn: props.setValidateInputMsg,
          validateKey: "orgName",
          elementId: "dw_signup_input_org_name",
          message: intlString(lang.ServerErrorCode[3008]),
        });
        return;
      }
      handleErrorNotify(
        error,
        lang.validateMsg.signup.defaultError,
        error.response?.data.error === 1024
          ? "validateMsg.freeTrial"
          : "ServerErrorCode"
      );
    },
  });
  const onClickRequest = () => {
    if (!mutationRequestTrial.idle) return;
    props.setValidateInputMsg({ ...intitalValidateMsg });
    let isValidated = false;
    if (checkEmpty(userInfo.email)) {
      props.setValidateInputMsg((prev) => ({
        ...prev,
        email: intlString(lang.validateMsg.profile.email.empty),
      }));
      isValidated = true;
    } else if (!checkEmailRegEx(userInfo.email)) {
      props.setValidateInputMsg((prev) => ({
        ...prev,
        email: intlString(lang.validateMsg.signup.invalidEmail),
      }));
      isValidated = true;
    }
    if (checkEmpty(userInfo.firstName)) {
      props.setValidateInputMsg((prev) => ({
        ...prev,
        firstName: intlString(lang.validateMsg.signup.firstname),
      }));
      isValidated = true;
    }
    if (checkEmpty(userInfo.lastName)) {
      props.setValidateInputMsg((prev) => ({
        ...prev,
        lastName: intlString(lang.validateMsg.signup.lastname),
      }));
      isValidated = true;
    }
    if (checkEmpty(userInfo.phone)) {
      props.setValidateInputMsg((prev) => ({
        ...prev,
        phoneNumber: intlString(lang.validateMsg.signup.phone.empty),
      }));
      isValidated = true;
    } else if (!isPhoneNumberValid(userInfo.phone)) {
      props.setValidateInputMsg((prev) => ({
        ...prev,
        phoneNumber: intlString(lang.validateMsg.profile.phone.validate),
      }));
      isValidated = true;
    }
    if (checkEmpty(userInfo.address)) {
      props.setValidateInputMsg((prev) => ({
        ...prev,
        address: intlString(lang.validateMsg.profile.address.empty),
      }));
      isValidated = true;
    }
    if (checkEmpty(userInfo.orgName)) {
      props.setValidateInputMsg((prev) => ({
        ...prev,
        orgName: intlString(lang.validateMsg.account.name),
      }));
      isValidated = true;
    } else if (
      userInfo.orgName.length < 3 ||
      userInfo.orgName.length > 100 ||
      orgNameRule(userInfo.orgName) ||
      orgNameAlphabetRule(userInfo.orgName)
    ) {
      props.setValidateInputMsg((prev) => ({
        ...prev,
        orgName: intlString(lang.validateMsg.profile.phone.validate),
      }));
      isValidated = true;
    }
    if (isValidated) {
      return;
    }
    mutationRequestTrial.mutate({
      payload: {
        ...userInfo,
        email: toTrim(userInfo.email),
        firstName: toTrim(userInfo.firstName),
        lastName: toTrim(userInfo.lastName),
        phone: toTrim(userInfo.phone),
        address: toTrim(userInfo.address),
        orgName: toTrim(userInfo.orgName),
        isCP: userInfo.isCP,
        businessType: toTrim(userInfo.businessType),
      },
    });
  };

  const onChangeUserInfo = (
    type: SIGNUP_UPDATE_TYPE,
    data: string | boolean
  ) => {
    if (
      type === SIGNUP_UPDATE_TYPE.PHONE_NUMBER ||
      type === SIGNUP_UPDATE_TYPE.FIRST_NAME ||
      type === SIGNUP_UPDATE_TYPE.LAST_NAME
    ) {
      props.onChangeUserInfo(type, data);
    } else {
      let key = "";
      switch (type) {
        case SIGNUP_UPDATE_TYPE.ADDRESS:
          key = "address";
          break;
        case SIGNUP_UPDATE_TYPE.ORG_NAME:
          key = "orgName";
          break;
        case SIGNUP_UPDATE_TYPE.EMAIL:
          key = "email";
          break;
        default:
          break;
      }
      if (!key) return;
      setFreeTrialInfo((prev) => ({
        ...prev,
        [key as keyof FreeTrialInfo]: data,
      }));
    }
  };

  useEffect(() => {
    const options = document.createElement("style");
    options.innerHTML = `
      .select-options {
        top: auto !important;
        bottom: 100% !important;
        margin-bottom: 40px !important;
      }
    `;
    document.head.appendChild(options);
    return () => {
      document.head.removeChild(options);
    };
  }, [userInfo.isCP]);
  return (
    <s.SignupInputWrapper>
      <s.InputWrapper>
        <p className="description free-trial-description">
          Digital Watchdog will send you an invitation email. Once you receive
          the email, approve it to join myDW. Then you can use myDW services for
          up to 60 days.
        </p>
        <InputItem
          label="Email"
          validateInputMsg={props.validateInputMsg}
          onChangeUserInfo={onChangeUserInfo}
          userInfo={userInfo}
          fieldKey="email"
          type={SIGNUP_UPDATE_TYPE.EMAIL}
          tabIndex={1}
        />

        <InputItem
          label="First Name"
          validateInputMsg={props.validateInputMsg}
          onChangeUserInfo={onChangeUserInfo}
          userInfo={userInfo}
          fieldKey="firstName"
          type={SIGNUP_UPDATE_TYPE.FIRST_NAME}
          tabIndex={2}
          maxLength={100}
        />

        <InputItem
          label="Last Name"
          validateInputMsg={props.validateInputMsg}
          onChangeUserInfo={onChangeUserInfo}
          userInfo={userInfo}
          fieldKey="lastName"
          type={SIGNUP_UPDATE_TYPE.LAST_NAME}
          tabIndex={3}
          maxLength={100}
        />

        <Phone
          validateInputMsg={props.validateInputMsg}
          onChangeUserInfo={onChangeUserInfo}
          userInfo={userInfo}
          requiredVerify={false}
          essential={true}
          tabIndex={4}
        />

        <ModalInput
          label="Address"
          noBold
          essential
          isValidated={!!props.validateInputMsg.address}
          validatedWord={props.validateInputMsg.address}
        >
          <AddressInput
            isLoaded={true}
            value={userInfo.address}
            onChange={(e) =>
              onChangeUserInfo(
                SIGNUP_UPDATE_TYPE.ADDRESS,
                e.currentTarget.value
              )
            }
            isValidated={!!props.validateInputMsg.address}
            onChangeLocationSelect={(
              address: google.maps.LatLngLiteral,
              addressString?: string,
              addressComponents?: AddressComponents | undefined
            ) => {
              if (addressComponents !== undefined) {
                onChangeUserInfo(
                  SIGNUP_UPDATE_TYPE.ADDRESS,
                  addressString ?? ""
                );
              }
            }}
            tabIndex={5}
            data-testid="auth-signup-step-address-input-change-user-info"
          />
        </ModalInput>
        <OrgName
          label="Desired Organization Name"
          validateInputMsg={props.validateInputMsg}
          onChangeUserInfo={onChangeUserInfo}
          userInfo={userInfo}
          fieldKey="orgName"
          type={SIGNUP_UPDATE_TYPE.ORG_NAME}
          tabIndex={6}
        />
      </s.InputWrapper>
      <s.SubmitWrapper className="free-trial-submit-wrapper">
        {/* <cs.ValidateWrapper>
                          <ValidateWord>{validateMsg}</ValidateWord>
                        </cs.ValidateWrapper> */}
        <BigButton
          id="dw_signup_button_signup"
          onClickBtn={onClickRequest}
          label="Request"
          data-testid="auth-signup-step-button-signup-click-request"
        />
      </s.SubmitWrapper>
      <ls.Bottom center style={{ marginTop: 20 }}>
        <UnderlineText
          id="dw_signup_button_forward_login"
          fontSize={14}
          color={COLORS.BUTTONS1}
          onClick={props.onClickBackLogin}
          data-testid="auth-signup-step-forward-login-click-back-login"
        >
          Go Back
        </UnderlineText>
      </ls.Bottom>
    </s.SignupInputWrapper>
  );
}
